import { color } from 'console-log-colors'
import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  retry,
} from '@reduxjs/toolkit/query/react'

import { env } from '../env'
import type {
  API_Community,
  API_CommunityConfig,
  API_GuestCard,
  API_MediaItem,
  API_Prospect,
  API_VariableRent,
  API_Unit,
  ID,
} from '../self-tour.service.types'
import { Community, fromSelfTour } from '../fromSelfTour'
import React from 'react'
import { PowerProSelect } from '.'
import { FloorPlanFilterOptions } from './powerpro.selectors'
import { logFactory } from '~/util/logger'
// Import MINUTE from its source module, not the `~/util` barrel: the barrel
// cycles back through `~/service`, and `SGT_REFRESH_OPTIONS` reads MINUTE at
// module-init time, so going through the barrel hits a temporal-dead-zone
// "Cannot access 'MINUTE' before initialization" error in prod builds.
import { MINUTE } from '~/util/time'

/**
 * Get the base URL for the PowerPro API. By making this a function, rather than
 * a constant, we're able to avoid build time cyclic dependency issues.
 */
export const BASE_URL = () => `${env.apiURL}/self-tour/api`

const logger = logFactory('PowerProAPI', color.yellowBright)

interface PowerProResponse<DataType> {
  body: DataType
}

/**
 * Returns the data (ie. the `body`) of the fetch response.
 */
function defaultResponseTransformer<DataType>(
  data: PowerProResponse<DataType>
) {
  return data?.body
}

const is404 = (error: { status?: number; originalStatus?: number }) =>
  error && (error.status === 404 || error.originalStatus === 404)

export function PowerProAPIFactory(
  baseQueryParam: BaseQueryFn = fetchBaseQuery({
    baseUrl: BASE_URL(),
    prepareHeaders: (headers) => {
      headers.set('Accept', 'application/json')
      return headers
    },
  }),
  retryCount = 4
) {
  // Retry request failures with exponential backoff.
  const baseQuery =
    retryCount < 1
      ? baseQueryParam
      : retry(baseQueryParam, { maxRetries: retryCount })

  const api = createApi({
    baseQuery: async (endpoint, meta, extraOptions) => {
      const url = typeof endpoint === 'string' ? endpoint : endpoint.url
      logger.info('🥚', url)
      try {
        const result = await baseQuery(endpoint, meta, extraOptions)
        logger.info('🐣', url, (result.data as any)?.body)
        return result
      } catch (e) {
        logger.info('❌🍳', url, 'FAILED')
        throw e
      }
    },
    reducerPath: 'PowerProAPI',
    endpoints: (build) => ({
      getVariableRentData: build.query<
        API_VariableRent[] | null,
        { guestCardUuid: ID }
      >({
        queryFn: async ({ guestCardUuid }, _api, _options, baseQuery) => {
          const url = `/unit-variable-rent/${guestCardUuid}`
          const method = 'GET'

          const result = await baseQuery({
            url,
            method,
          })

          const data: API_VariableRent[] | null = is404(result.error as any)
            ? null
            : (result.data as API_VariableRent[])

          return { data }
        },
        extraOptions: {
          // If the community doesn't support variable rent data, then this
          // request will 404 so we don't want to retry on error.
          maxRetries: 0,
        },
      }),
      /**
       * Get a community's configuration details (example contact details,
       * address, etc).
       */
      getCommunityConfig: build.query<
        API_CommunityConfig,
        { guestCardUuid: ID }
      >({
        query: ({ guestCardUuid }) => `/${guestCardUuid}/config`,
        transformResponse: defaultResponseTransformer,
      }),
      /**
       * Get a unit info  by community ID and unit id.
       */
      getCommunityUnits: build.query<
        API_Unit[],
        { communityId: ID; unitIds: ID[] }
      >({
        query: ({ communityId, unitIds }) => {
          const params = (unitIds ?? []).map((id) => `unitId=${id}`).join('&')
          return `/communities/${communityId}/units?${params}`
        },
        transformResponse: defaultResponseTransformer,
      }),
      /**
       * Get a community's configuration details by community ID(example contact details,
       * address, etc).
       */
      getCommunityConfigByCommunityId: build.query<
        API_CommunityConfig,
        { communityId: ID }
      >({
        query: ({ communityId }) => `/community/${communityId}/config`,
        transformResponse: defaultResponseTransformer,
      }),
      /**
       * Get the details about a community by community ID such as the number of units, amenities,
       * floor plans, etc.
       */
      getCommunityByCommunityId: build.query<
        API_Community,
        { communityId: ID }
      >({
        query: ({ communityId }) => `/community/${communityId}/community`,
        transformResponse: defaultResponseTransformer,
      }),
      /**
       * Get a community's default assests such as the ADA and HUD logos in the
       * footer.
       */
      getCommunityDefaultAssets: build.query<
        API_MediaItem[],
        { guestCardUuid: ID }
      >({
        query: ({ guestCardUuid }) => `/${guestCardUuid}/default-assets`,
        transformResponse: defaultResponseTransformer,
      }),
      /**
       * Get the details about a community such as the number of units, amenities,
       * floor plans, etc.
       */
      getCommunityDetails: build.query<API_Community, { guestCardUuid: ID }>({
        query: ({ guestCardUuid }) => `/${guestCardUuid}/community`,
        transformResponse: defaultResponseTransformer,
      }),
      /**
       * Get the details about a guest card such as the units the user has seen or
       * is interested in.
       */
      getGuestCardDetails: build.query<API_GuestCard, { guestCardUuid: ID }>({
        query: ({ guestCardUuid }) => `/${guestCardUuid}/guest-card`,
        transformResponse: defaultResponseTransformer,
      }),
      /**
       * Update details about a user's preferences. This is used to update the
       * user's pre-tour checkin preferences such as move dates, occupant info,
       * etc.
       */
      setGuestCardDetails: build.mutation<
        boolean,
        {
          guestCardUuid: ID
          data: Partial<API_GuestCard>
        }
      >({
        query: ({ guestCardUuid, data }) => ({
          url: `/${guestCardUuid}`,
          method: 'PATCH',
          body: data,
        }),
        transformResponse: defaultResponseTransformer,
        onQueryStarted: async (
          { guestCardUuid, data },
          { dispatch, queryFulfilled }
        ) => {
          const patch = dispatch(
            api.util.updateQueryData(
              'getGuestCardDetails',
              { guestCardUuid },
              (draft) =>
                Object.keys(data).forEach((key) => {
                  ;(draft as Record<string, any>)[key] =
                    data[key as keyof API_GuestCard]
                })
            )
          )

          try {
            await queryFulfilled
          } catch (e) {
            logger.info(
              `/${guestCardUuid}`,
              'FAILED',
              '⏪ Reverting to previous state'
            )
            patch.undo()
          }
        },
      }),
      /**
       * Get the details about the primary prospect on a guest card such as their
       * name, email, phone number, etc.
       */
      getPrimaryProspect: build.query<API_Prospect, { guestCardUuid: ID }>({
        query: ({ guestCardUuid }) => `/${guestCardUuid}/guest-card/prospect`,
        transformResponse: defaultResponseTransformer,
      }),
      /**
       * Track the user opening their guest card.
       */
      trackUserAccess: build.mutation<boolean, { guestCardUuid: ID }>({
        query: ({ guestCardUuid }) => ({
          url: `/track/${guestCardUuid}`,
          method: 'POST',
        }),
        transformResponse: defaultResponseTransformer,
      }),
      /**
       * Tell the backend that a user has completed the verification process.
       *
       * @parma guestCardUuid - The id of the current guest card
       * @param prospectId - The id of the user
       * @param inquiryId - The id of the verificaton inquiry returned by the
       *   Persona API.
       */
      setUserVerificationComplete: build.mutation<
        boolean,
        {
          guestCardUuid: ID
          prospectId: ID
          inquiryId: ID
        }
      >({
        query: ({ prospectId, guestCardUuid, inquiryId }) => ({
          url: `/${guestCardUuid}/guest-card/prospect/id-inquiry`,
          method: 'POST',
          body: {
            prospectiveTenantId: prospectId,
            thirdPartyKey: inquiryId,
            status: 'COMPLETED',
            // TEMP This is currently required by the API but will be removed in the
            // future. Hardcoding it until it is removed.
            type: 'Selfie',
          },
        }),
        transformResponse: defaultResponseTransformer,
        // No Optimistic Updates: We want to force the user to wait until the
        // verification state has been saved or they'll get booted back to the
        // verification again if we failed to save the results.
        onQueryStarted: async (
          { guestCardUuid, inquiryId },
          { dispatch, queryFulfilled }
        ) => {
          try {
            await queryFulfilled
            dispatch(
              api.util.updateQueryData(
                'getGuestCardDetails',
                { guestCardUuid },
                (draft) => {
                  draft.primaryProspect.idInquiry = {
                    ...(draft.primaryProspect.idInquiry || {}),
                    id: 0,
                    status: 'COMPLETED',
                    thirdPartyKey: inquiryId,
                  }
                }
              )
            )
          } catch (e) {
            logger.error('Failed to save user verification', e)
          }
        },
      }),
      /**
       * Add a unit to the user's list of favorite units.
       */
      addUnitToFavorites: build.mutation<
        boolean,
        { guestCardUuid: ID; unitId: ID }
      >({
        query: ({ guestCardUuid, unitId }) => ({
          url: `/${guestCardUuid}/interested-units/${unitId}`,
          method: 'POST',
        }),
        transformResponse: defaultResponseTransformer,
        onQueryStarted: async (
          { guestCardUuid, unitId },
          { dispatch, queryFulfilled }
        ) => {
          const patch = dispatch(
            api.util.updateQueryData(
              'getGuestCardDetails',
              { guestCardUuid },
              (draft) => {
                draft.interestedUnitIds = [
                  ...(draft.interestedUnitIds?.filter((id) => id !== unitId) ||
                    []),
                  unitId,
                ]
              }
            )
          )

          try {
            await queryFulfilled
          } catch (e) {
            logger.info(
              '⏪',
              {
                url: `/${guestCardUuid}/interested-units/${unitId}`,
                method: 'POST',
              },
              'FAILED'
            )
            patch.undo()
          }
        },
      }),
      /**
       * Add a unit to the user's list of favorite units.
       */
      removeUnitFromFavorites: build.mutation<
        boolean,
        { guestCardUuid: ID; unitId: ID }
      >({
        query: ({ guestCardUuid, unitId }) => ({
          url: `/${guestCardUuid}/interested-units/${unitId}`,
          method: 'DELETE',
        }),
        transformResponse: defaultResponseTransformer,
        onQueryStarted: async (
          { guestCardUuid, unitId },
          { dispatch, queryFulfilled }
        ) => {
          const patch = dispatch(
            api.util.updateQueryData(
              'getGuestCardDetails',
              { guestCardUuid },
              (draft) => {
                draft.interestedUnitIds =
                  draft.interestedUnitIds?.filter((id) => id !== unitId) || []
              }
            )
          )

          try {
            await queryFulfilled
          } catch (e) {
            logger.info(
              '⏪',
              {
                url: `/${guestCardUuid}/interested-units/${unitId}`,
                method: 'DELETE',
              },
              'FAILED'
            )
            console.log('[PowerProService] patch before', patch)
            patch.undo()
            console.log('[PowerProService] patch after', patch)
          }
        },
      }),
    }),
  })
  return api
}

export type PowerProAPIType = ReturnType<typeof PowerProAPIFactory>

export const PowerProAPIContext = React.createContext<null | PowerProAPIType>(
  null
)
export const PowerProAPIProvider = PowerProAPIContext.Provider
export const usePowerProAPI = function () {
  const api = React.useContext(PowerProAPIContext) as PowerProAPIType
  if (!api)
    throw new Error(
      'PowerProAPI not provided. You must wrap your components in a PowerProAPIProvider, ideally using `<StoreProvider value={StoreFactory()} .../>`'
    )
  return api
}

/**
 * Let the API know that the user looked at the guest card.
 */
export const useTrackGuestCardOpen = (guestCardUuid: ID) => {
  const api = usePowerProAPI()
  const [trackAccess] = api.useTrackUserAccessMutation()
  React.useEffect(() => {
    trackAccess({ guestCardUuid })
  }, [guestCardUuid, trackAccess])
}

/**
 * Get the community's config. This will use `fromSelfTour` to do transforms on
 * the data that make it easier to use.
 */
export const useGetCommunityConfigQuery = ({
  guestCardUuid,
}: {
  guestCardUuid: ID
}) => {
  const api = usePowerProAPI()
  const { data, ...result } = api.useGetCommunityConfigQuery({
    guestCardUuid,
  })
  return {
    ...result,
    data: !!data ? fromSelfTour.config(data) : undefined,
  }
}

/**
 * Get the community's config. This will use `fromSelfTour` to do transforms on
 * the data that make it easier to use.
 */
export const useGetCommunityConfigByCommunityIdQuery = ({
  communityId,
}: {
  communityId: ID
}) => {
  const api = usePowerProAPI()
  const { data, ...result } = api.useGetCommunityConfigByCommunityIdQuery({
    communityId,
  })
  return {
    ...result,
    data: !!data ? fromSelfTour.config(data) : undefined,
  }
}
export const useGetCommunityUnitsQuery = ({
  communityId,
  unitIds,
}: {
  communityId: ID
  unitIds: ID[]
}) => {
  const api = usePowerProAPI()
  const { data, ...result } = api.useGetCommunityUnitsQuery({
    communityId: communityId,
    unitIds,
  })

  return {
    ...result,
    data: data
      ? data.map((u) =>
          fromSelfTour.unit({
            unit: u,
            // TODO These properties need to be passed.
            features: [],
            floorPlates: [],
            floorPlans: [],
            leaseTerms: [],
            variableRentData: null,
            mediaTags: [],
          })
        )
      : undefined,
  }
}
/**
 * Get the community information. This will use `fromSelfTour` to do transforms on
 * the data that make it easier to use.
 */
export const useGetCommunityByCommunityIdQuery = ({
  communityId,
}: {
  communityId: ID
}) => {
  const api = usePowerProAPI()
  const { data, ...result } = api.useGetCommunityByCommunityIdQuery({
    communityId,
  })
  return {
    ...result,
    data: !!data
      ? fromSelfTour.community(
          data,
          // TODO We will need an API to get variable rent by community ID
          null
        )
      : undefined,
  }
}

/**
 * Get the community's variable rent data, if they use variable rent.
 */
export const useGetVariableRentDataQuery = ({
  config,
  configLoading,
  guestCardUuid,
}: {
  // TODO (streetwise#705): This should be CommunityConfig (the transformed type) since
  // the value comes from useGetCommunityConfigQuery which already applies fromSelfTour.config().
  // Changing it requires updating fromSelfTour.guestCard() and its call sites/tests.
  config: API_CommunityConfig | undefined
  configLoading: boolean
  guestCardUuid: ID
}) => {
  const api = usePowerProAPI()
  const [
    getVariableRentData,
    {
      isLoading: variableRentLoading,
      isUninitialized: uninitialized,
      data: _variableRent,
    },
  ] = api.useLazyGetVariableRentDataQuery()

  React.useEffect(() => {
    // If the community uses variable rent, request the rent data so we can merge
    // it into the community data. This simplifies the work of displaying unit
    // pricing throughout the app.
    if (uninitialized && !configLoading && config?.isVariableRentEnabled) {
      getVariableRentData(
        { guestCardUuid },
        // prefer cached value
        true
      )
    }
  }, [
    config?.isVariableRentEnabled,
    configLoading,
    getVariableRentData,
    guestCardUuid,
    uninitialized,
  ])

  const variableRent =
    !configLoading && !config?.isVariableRentEnabled
      ? null
      : configLoading || uninitialized || variableRentLoading
      ? undefined
      : _variableRent

  return {
    data: variableRent,
    isLoading: configLoading || variableRentLoading,
  }
}

/**
 * Get the community details. This will also request the community config and
 * the community variable rent data (if the community has variable rent enabled).
 * Variable rent data will be used to generate the prices on `community.units`.
 * The config will be returned as is.
 */
export const useGetCommunityDetailsQuery = ({
  guestCardUuid,
}: {
  guestCardUuid: ID
}) => {
  const api = usePowerProAPI()

  const { isLoading: configLoading, data: config } = useGetCommunityConfigQuery(
    { guestCardUuid }
  )

  // Load the variable rent data if the community uses variable rent pricing.
  const { data: variableRent, isLoading: variableRentLoading } =
    useGetVariableRentDataQuery({
      guestCardUuid,
      config,
      configLoading,
    })

  // Get the community config as well because it will tell us if this community
  // has variable rent pricing enabled.
  const { isLoading: communityLoading, data: community } =
    api.useGetCommunityDetailsQuery({ guestCardUuid })

  const isLoading = configLoading || communityLoading || variableRentLoading
  return {
    isLoading,
    data: {
      config,
      community:
        !isLoading && !!community
          ? fromSelfTour.community(community!, variableRent!)
          : undefined,
    },
  }
}

/**
 * `rtk-query` subscription options that control how often the guest card is
 * re-fetched while a prospect is on the tour screen (see issue #955).
 */
export interface GuestCardRefreshOptions {
  pollingInterval?: number
  refetchOnFocus?: boolean
  refetchOnReconnect?: boolean
  skipPollingIfUnfocused?: boolean
}

/**
 * The refresh cadence used while a prospect is on the SGT tour screen: poll once
 * a minute and refresh on focus/reconnect, but stop polling while the tab/app is
 * unfocused so an abandoned or backgrounded session doesn't keep hitting the API.
 */
export const SGT_REFRESH_OPTIONS: GuestCardRefreshOptions = {
  pollingInterval: MINUTE,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  skipPollingIfUnfocused: true,
}

/**
 * Whether the guest card should be kept on a background refresh cadence — true
 * only while there's an appointment that hasn't ended yet (an active or upcoming
 * self-guided tour).
 *
 * - An SGT with a current/upcoming appointment → refresh, so an apartment added
 *   mid-tour (and its lock codes) appears without a reload.
 * - A tour-to-go (no appointment) → nothing to refresh.
 * - An SGT whose appointment has already ended → nothing left to refresh, so we
 *   stop polling rather than hit the API forever on a finished tour.
 *
 * An appointment with no `scheduledEnd` is treated as not-yet-ended, matching how
 * `getActiveAppointment` considers an open-ended appointment still active.
 */
export function sgtShouldRefresh(
  appointments: readonly { scheduledEnd?: string }[] | null | undefined
): boolean {
  const now = new Date()
  return !!appointments?.some(
    (a) => !a.scheduledEnd || new Date(a.scheduledEnd) > now
  )
}

/**
 * Get the user's guest card data. This will use `fromSelfTour` to do any
 * transforms that make the data easier to use.
 */
export const useGetGuestCardDetailsQuery = (
  {
    guestCardUuid,
  }: {
    guestCardUuid: ID
  },
  /**
   * Optional `rtk-query` subscription options. The SGT tour screen passes
   * `pollingInterval` / `refetchOnFocus` so an apartment added mid-tour (and its
   * lock codes) is picked up without losing access to units already on the tour
   * (see issue #955). Defaults to no polling for every other consumer.
   */
  options?: GuestCardRefreshOptions
) => {
  const api = usePowerProAPI()
  const {
    isLoading: communityLoading,
    data: { config, community },
  } = useGetCommunityDetailsQuery({
    guestCardUuid,
  })
  const { isLoading: guestCardLoading, data: guestCard } =
    api.useGetGuestCardDetailsQuery({ guestCardUuid }, options)
  return {
    isLoading: guestCardLoading || communityLoading,
    data:
      !!guestCard && !!config
        ? fromSelfTour.guestCard(
            guestCard,
            // TODO Update views to request this data separately
            community?.units,
            community?.leaseTerms,
            community?.quoteCategories,
            config
          )
        : undefined,
  }
}

/**
 * Get the details about the primary prospect on a guest card.
 */
export const useGetPrimaryProspectQuery = ({
  guestCardUuid,
}: {
  guestCardUuid: ID
}) => usePowerProAPI().useGetPrimaryProspectQuery({ guestCardUuid })

/**
 * Set check in details on the guest card such as the user's move in dates and
 * bed/bath count preferences. Returns a tuple with the first parameter being a
 * callback you can use to make the mutation request and the second parameter
 * being meta data about the request state.
 *
 * @see
 * https://redux-toolkit.js.org/rtk-query/usage/mutations#performing-mutations-with-react-hooks
 */
export const useSetGuestCardDetails = (guestCardUuid: ID) => {
  const [trigger, meta] = usePowerProAPI().useSetGuestCardDetailsMutation()
  type Details = Parameters<typeof trigger>[0]['data']

  return [
    /**
     * @param data The properties on the guest card to set.
     */
    (data: Details) => trigger({ guestCardUuid, data }),
    meta,
  ] as const
}

/**
 * Returns a function you can use to add a unit to the user's list of favorites.
 */
export function useAddUnitToFavorites(guestCardUuid: ID) {
  const [trigger, meta] = usePowerProAPI().useAddUnitToFavoritesMutation()

  return [
    /**
     * @param data The properties on the guest card to set.
     */
    (unitId: ID) => trigger({ guestCardUuid, unitId }),
    meta,
  ] as const
}

/**
 * Returns a function you can use to remove a unit from the user's list of favorites.
 */
export function useRemoveUnitFromFavorites(guestCardUuid: ID) {
  const [trigger, meta] = usePowerProAPI().useRemoveUnitFromFavoritesMutation()

  return [
    /**
     * @param data The properties on the guest card to set.
     */
    (unitId: ID) => trigger({ guestCardUuid, unitId }),
    meta,
  ] as const
}

// TODO Refactor `SearchFloorPlan` to remove the need for this hook. We
// shouldn't need to maintain a separate state with the filtered list of floor
// plans. Instead we should just use the PowerProSelect inline in the page
// component. Additionally, it would be good to keep the unit data separate from
// the floor plan data so as to be more consistent with the rest of the app.
export function useFloorPlanFilter(community: Community | undefined) {
  // Merge in the unit data
  const initalFloorPlans = PowerProSelect.filterCommunityFloorPlans(community)
  const [filteredFloorPlans, setFilteredFloorPlans] =
    React.useState(initalFloorPlans)
  const allFloorPlans = initalFloorPlans

  return {
    getFiltedFloorPlans: React.useCallback(
      (filter?: FloorPlanFilterOptions) => {
        const updated = PowerProSelect.filterCommunityFloorPlans(
          community,
          filter
        )
        setFilteredFloorPlans(updated)
        return updated
      },
      [setFilteredFloorPlans, community]
    ),
    filteredFloorPlans,
    allFloorPlans,
  }
}

/**
 * Save the user's verification status to the guest card.
 */
export function useSaveUserIdVerification(
  guestCardUuid: ID,
  prospectId: ID | undefined
) {
  const [trigger, meta] =
    usePowerProAPI().useSetUserVerificationCompleteMutation()
  return [
    /**
     * @param inquiryId The inquiry id retrieved from the Persona API
     */
    async (inquiryId: ID) => {
      if (prospectId) {
        const result = await trigger({ guestCardUuid, prospectId, inquiryId })
        if (result.error) throw new Error('Unable to save verification state')
        return result
      } else {
        // prettier-ignore
        logger.error(
          'Propsect Id missing when attempting to save inquiry id',
          '\n  propsectId:', prospectId,
          '\n  guestCardUuid:', guestCardUuid,
          '\n  inquiryId:', inquiryId
        )
        throw new Error('Unable to verify the current user')
      }
    },
    meta,
  ] as const
}
